// admin/pages/AccountDeletions.tsx — pending GDPR deletion queue.
//
// Two admin actions per row:
//   - "Force delete now"  → admin:force_account_deletion (skip cooldown)
//   - "Cancel deletion"   → admin:cancel_account_deletion
//
// Both are destructive in different ways; the page surfaces the user's
// email, scheduledAt countdown, and a confirmation step before either
// admin override fires.

import {
  useCallback,
  useEffect,
  useMemo,
  useState,
  type CSSProperties,
  type RefObject,
} from "react";
import type { Socket } from "socket.io-client";
import { styles, tokens } from "../styles";
import { RpcError } from "../socket/rpc";
import type {
  AdminAccountDeletionQueueAck,
  AdminAccountDeletionRow,
} from "../store/types";

interface AccountDeletionsPageProps {
  socket: RefObject<Socket | null>;
}

const PAGE_SIZES = [25, 50, 100] as const;

interface CancelAck {
  ok: boolean;
  error?: string;
}

export default function AccountDeletions({ socket }: AccountDeletionsPageProps) {
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState<(typeof PAGE_SIZES)[number]>(25);
  const [rows, setRows] = useState<AdminAccountDeletionRow[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [version, setVersion] = useState(0);

  useEffect(() => {
    const sock = socket.current;
    if (!sock) return;
    let cancelled = false;
    const requestId =
      typeof crypto !== "undefined" && "randomUUID" in crypto
        ? crypto.randomUUID()
        : `r-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
    setLoading(true);
    setError(null);
    (async () => {
      try {
        const result = await new Promise<{
          requestId: string;
          deletions: AdminAccountDeletionRow[];
          total: number;
          page: number;
          pageSize: number;
        }>((resolve, reject) => {
          if (!sock.connected) {
            reject(new RpcError("socket_not_connected"));
            return;
          }
          let settled = false;
          const timeout = window.setTimeout(() => {
            if (settled) return;
            settled = true;
            reject(new RpcError("timeout"));
          }, 15_000);
          sock.emit(
            "admin:account_deletion_queue_query",
            { requestId, page, pageSize },
            (ack: AdminAccountDeletionQueueAck) => {
              if (settled) return;
              settled = true;
              window.clearTimeout(timeout);
              if (ack.ok) resolve(ack);
              else reject(new RpcError(ack.error));
            },
          );
        });
        if (cancelled) return;
        if (result.requestId !== requestId) return;
        setRows(result.deletions);
        setTotal(result.total);
      } catch (err) {
        if (cancelled) return;
        setError(err instanceof Error ? err.message : String(err));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [socket, page, pageSize, version]);

  const refresh = useCallback(() => setVersion((v) => v + 1), []);

  const forceDelete = useCallback(
    async (userId: string) => {
      const sock = socket.current;
      if (!sock) return;
      try {
        await emitWithAck<CancelAck>(sock, "admin:force_account_deletion", {
          userId,
        });
        refresh();
      } catch (err) {
        setError(err instanceof Error ? err.message : String(err));
      }
    },
    [socket, refresh],
  );

  const cancelDeletion = useCallback(
    async (userId: string) => {
      const sock = socket.current;
      if (!sock) return;
      try {
        await emitWithAck<CancelAck>(sock, "admin:cancel_account_deletion", {
          userId,
        });
        refresh();
      } catch (err) {
        setError(err instanceof Error ? err.message : String(err));
      }
    },
    [socket, refresh],
  );

  const totalPages = useMemo(
    () => Math.max(1, Math.ceil(total / pageSize)),
    [total, pageSize],
  );

  return (
    <div>
      <h2 style={styles.pageTitle}>Account deletions</h2>
      <p style={styles.pageSub}>
        {new Intl.NumberFormat("en-US").format(total)} pending. Cron sweeps
        hourly; each row finalises 14 days after the user's request.
      </p>

      <div style={styles.controlsRow}>
        <div />
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <label style={controlLabelStyle}>
            Rows per page
            <select
              value={pageSize}
              onChange={(e) => {
                setPageSize(Number(e.target.value) as (typeof PAGE_SIZES)[number]);
                setPage(1);
              }}
              style={styles.select}
            >
              {PAGE_SIZES.map((n) => (
                <option key={n} value={n}>
                  {n}
                </option>
              ))}
            </select>
          </label>
        </div>
      </div>

      <div style={styles.tableWrap}>
        <table style={styles.table}>
          <thead>
            <tr>
              <th style={styles.th}>User</th>
              <th style={styles.th}>Requested</th>
              <th style={styles.th}>Finalises</th>
              <th style={styles.th}>Countdown</th>
              <th style={{ ...styles.th, textAlign: "right" }}></th>
            </tr>
          </thead>
          <tbody>
            {loading && rows.length === 0 ? (
              <tr>
                <td colSpan={5} style={styles.empty}>
                  Loading…
                </td>
              </tr>
            ) : error ? (
              <tr>
                <td
                  colSpan={5}
                  style={{ ...styles.empty, color: tokens.destructive }}
                >
                  {error}
                </td>
              </tr>
            ) : rows.length === 0 ? (
              <tr>
                <td colSpan={5} style={styles.empty}>
                  No pending deletions.
                </td>
              </tr>
            ) : (
              rows.map((r) => (
                <Row
                  key={r.id}
                  row={r}
                  onForce={() => void forceDelete(r.userId)}
                  onCancel={() => void cancelDeletion(r.userId)}
                />
              ))
            )}
          </tbody>
        </table>
      </div>

      <div style={styles.pager}>
        <div>
          Page {page} of {totalPages}
        </div>
        <div style={{ display: "flex", gap: 8 }}>
          <button
            type="button"
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            disabled={page <= 1 || loading}
            style={{
              ...styles.pagerBtn,
              ...(page <= 1 || loading ? styles.pagerBtnDisabled : {}),
            }}
          >
            Prev
          </button>
          <button
            type="button"
            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            disabled={page >= totalPages || loading}
            style={{
              ...styles.pagerBtn,
              ...(page >= totalPages || loading ? styles.pagerBtnDisabled : {}),
            }}
          >
            Next
          </button>
        </div>
      </div>
    </div>
  );
}

function Row({
  row,
  onForce,
  onCancel,
}: {
  row: AdminAccountDeletionRow;
  onForce: () => void;
  onCancel: () => void;
}) {
  const [confirming, setConfirming] = useState<"force" | "cancel" | null>(null);
  const countdown = useMemo(() => formatCountdown(row.scheduledAt), [row.scheduledAt]);
  return (
    <tr>
      <td style={styles.td}>
        <div style={emailCellStyle}>{row.userEmail}</div>
        {row.userName && <div style={subStyle}>{row.userName}</div>}
      </td>
      <td style={{ ...styles.td, ...styles.tdMono, fontSize: 12 }}>
        {formatWhen(row.requestedAt)}
      </td>
      <td style={{ ...styles.td, ...styles.tdMono, fontSize: 12 }}>
        {formatWhen(row.scheduledAt)}
      </td>
      <td style={styles.td}>
        <span style={countdownStyle}>{countdown}</span>
      </td>
      <td style={{ ...styles.td, textAlign: "right" }}>
        {confirming === "force" ? (
          <div style={confirmRow}>
            <span style={confirmText}>Force-delete now?</span>
            <button
              type="button"
              style={destructiveBtn}
              onClick={() => {
                setConfirming(null);
                onForce();
              }}
            >
              Yes, delete
            </button>
            <button type="button" style={cancelBtn} onClick={() => setConfirming(null)}>
              No
            </button>
          </div>
        ) : confirming === "cancel" ? (
          <div style={confirmRow}>
            <span style={confirmText}>Cancel deletion?</span>
            <button
              type="button"
              style={primaryBtn}
              onClick={() => {
                setConfirming(null);
                onCancel();
              }}
            >
              Yes
            </button>
            <button type="button" style={cancelBtn} onClick={() => setConfirming(null)}>
              No
            </button>
          </div>
        ) : (
          <div style={{ display: "inline-flex", gap: 6 }}>
            <button
              type="button"
              style={destructiveBtn}
              onClick={() => setConfirming("force")}
              title="Skip the 14-day wait and anonymise immediately"
            >
              Force delete
            </button>
            <button
              type="button"
              style={primaryBtn}
              onClick={() => setConfirming("cancel")}
              title="Revert the deletion request"
            >
              Cancel
            </button>
          </div>
        )}
      </td>
    </tr>
  );
}

function formatWhen(iso: string): string {
  try {
    const d = new Date(iso);
    if (!Number.isFinite(d.getTime())) return "—";
    return `${d.toISOString().slice(0, 10)} ${d.toISOString().slice(11, 16)}`;
  } catch {
    return iso;
  }
}

function formatCountdown(iso: string): string {
  try {
    const target = new Date(iso).getTime();
    const now = Date.now();
    const diffMs = target - now;
    if (diffMs <= 0) return "due now";
    const days = Math.floor(diffMs / (24 * 60 * 60 * 1000));
    const hours = Math.floor((diffMs % (24 * 60 * 60 * 1000)) / (60 * 60 * 1000));
    if (days > 0) return `${days}d ${hours}h`;
    return `${hours}h`;
  } catch {
    return "—";
  }
}

function emitWithAck<TAck>(
  socket: Socket,
  event: string,
  payload: unknown,
): Promise<TAck> {
  return new Promise((resolve, reject) => {
    if (!socket.connected) {
      reject(new RpcError("socket_not_connected"));
      return;
    }
    let settled = false;
    const timeout = window.setTimeout(() => {
      if (settled) return;
      settled = true;
      reject(new RpcError("timeout"));
    }, 15_000);
    socket.emit(event, payload, (ack: TAck) => {
      if (settled) return;
      settled = true;
      window.clearTimeout(timeout);
      const o = ack as { ok?: boolean; error?: string };
      if (o && o.ok) resolve(ack);
      else reject(new RpcError(o?.error ?? "unknown_error"));
    });
  });
}

const controlLabelStyle: CSSProperties = {
  color: tokens.inkMuted,
  fontSize: 13,
  display: "flex",
  alignItems: "center",
  gap: 8,
};

const emailCellStyle: CSSProperties = {
  color: tokens.inkPrimary,
  fontSize: 13,
};

const subStyle: CSSProperties = {
  color: tokens.inkMuted,
  fontSize: 11,
  marginTop: 2,
};

const countdownStyle: CSSProperties = {
  fontFamily: tokens.fontMono,
  fontSize: 12,
  color: tokens.warn,
};

const destructiveBtn: CSSProperties = {
  background: "transparent",
  border: `1px solid ${tokens.destructive}`,
  color: tokens.destructive,
  padding: "4px 10px",
  fontSize: 12,
  borderRadius: 4,
  cursor: "pointer",
  fontFamily: tokens.fontBody,
};

const primaryBtn: CSSProperties = {
  background: "transparent",
  border: `1px solid ${tokens.accent}`,
  color: tokens.accent,
  padding: "4px 10px",
  fontSize: 12,
  borderRadius: 4,
  cursor: "pointer",
  fontFamily: tokens.fontBody,
};

const cancelBtn: CSSProperties = {
  background: "transparent",
  border: `1px solid ${tokens.border}`,
  color: tokens.inkMuted,
  padding: "4px 10px",
  fontSize: 12,
  borderRadius: 4,
  cursor: "pointer",
  fontFamily: tokens.fontBody,
};

const confirmRow: CSSProperties = {
  display: "inline-flex",
  alignItems: "center",
  gap: 6,
};

const confirmText: CSSProperties = {
  fontSize: 12,
  color: tokens.inkMuted,
  marginRight: 4,
};
